import { HowDeterminedButton } from "@components/provenance/HowDeterminedButton";
import { Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

function staleReasonsLabel(reasons: string[]): string {
  const seen = new Set(reasons);
  const parts: string[] = [];
  if (seen.has("rubric_changed")) parts.push("matrix edited");
  if (seen.has("dimension_changed")) parts.push("dimension edited");
  if (seen.has("ideal_score_changed")) parts.push("ideal score changed");
  return parts.length > 0 ? parts.join(", ") : "criteria changed since this score";
}

export const ANCHORS: Record<number, string> = {
  1: "Clear evidence of not meeting requirement",
  2: "Partially meets; material gaps",
  3: "Meets requirement (baseline hire standard)",
  4: "Strongly meets; above standard",
  5: "Significantly exceeds; standout",
};

interface Props {
  initialScore: number | null;
  initialEvidence: string | null;
  saving: boolean;
  onSave: (score: number, evidence: string) => Promise<void> | void;
  onCancel: () => void;
  candidateName?: string;
  dimensionName?: string;
  idealScore?: number | null;
  campaignCandidateId?: string;
  // When the cell is stale, show the live matrix body above the score
  // buttons so the recruiter re-evaluates against the current criteria.
  currentBandBody?: string | null;
  staleReasons?: string[];
}

export function MatrixCellEditor({
  initialScore,
  initialEvidence,
  saving,
  onSave,
  onCancel,
  candidateName,
  dimensionName,
  idealScore,
  campaignCandidateId,
  currentBandBody,
  staleReasons,
}: Props) {
  const [score, setScore] = useState<number | null>(initialScore);
  const [evidence, setEvidence] = useState<string>(initialEvidence ?? "");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const canSave = score !== null && evidence.trim().length >= 10;
  const hasHeader = Boolean(candidateName || dimensionName);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Score candidate on capability dimension"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <button
        type="button"
        aria-label="Close score editor"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onCancel}
      />
      <div
        ref={containerRef}
        className="relative z-50 w-full max-w-md rounded-lg bg-card p-5 shadow-xl"
        data-testid="matrix-cell-editor"
      >
        {hasHeader && (
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              {dimensionName && (
                <h3 className="text-base font-semibold text-text-primary">
                  {dimensionName}
                  {idealScore != null && (
                    <span className="ml-2 text-xs font-normal text-text-faint">
                      ideal {idealScore}/10
                    </span>
                  )}
                </h3>
              )}
              {candidateName && (
                <p className="mt-0.5 text-xs text-text-muted">Scoring {candidateName}</p>
              )}
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onCancel}
              className="rounded p-1 text-text-faint hover:bg-card-hover"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {currentBandBody && (
          <div
            className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 dark:border-amber-800 dark:bg-amber-900/20"
            data-testid="current-rubric-panel"
          >
            <p className="mb-1 text-xs font-medium text-amber-900 dark:text-amber-300">
              Current criteria
              {staleReasons && staleReasons.length > 0 && (
                <span className="ml-2 font-normal text-amber-700 dark:text-amber-400">
                  · {staleReasonsLabel(staleReasons)}
                </span>
              )}
            </p>
            <p className="text-xs text-amber-900 dark:text-amber-200">{currentBandBody}</p>
          </div>
        )}

        <div className="mb-2 text-xs font-medium text-text-secondary">Score</div>
        <div className="mb-2 flex gap-1.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              type="button"
              title={ANCHORS[s]}
              aria-label={`Score ${s}: ${ANCHORS[s]}`}
              onClick={() => setScore(s)}
              className={`h-9 w-9 rounded text-sm font-medium ${
                score === s
                  ? "bg-accent text-white"
                  : "bg-card-alt text-text-secondary hover:bg-card-hover"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <p className="mb-3 min-h-[1.25rem] text-xs text-text-faint">
          {score !== null ? ANCHORS[score] : "Pick a score to see its anchor description."}
        </p>

        <label
          htmlFor="matrix-cell-evidence"
          className="mb-1 block text-xs font-medium text-text-secondary"
        >
          Evidence
        </label>
        <textarea
          id="matrix-cell-evidence"
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          placeholder="Evidence (min 10 chars) — what backs this score?"
          rows={10}
          className="mb-3 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />

        {campaignCandidateId && (
          <div className="mb-3">
            <HowDeterminedButton
              campaignCandidateId={campaignCandidateId}
              candidateName={candidateName}
              subject={{ kind: "assessment" }}
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded px-3 py-1.5 text-xs text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => canSave && onSave(score, evidence.trim())}
            disabled={!canSave || saving}
            className="flex items-center gap-1 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
